import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Tab,
    Tabs,
    CircularProgress,
    Tooltip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    IconCheck,
    IconX,
    IconEye,
    IconCalendar,
    IconUser,
    IconLeaf,
    IconMapPin,
    IconPhoto,
    IconVideo,
    IconNotes,
    IconChevronRight,
    IconExternalLink,
    IconAlertCircle,
    IconFilter,
    IconZoomIn,
    IconZoomOut,
    IconRefresh
} from '@tabler/icons-react';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import { toast } from 'react-toastify';

const StatusBadge = ({ status }) => {
    let config = {
        background: '#F1F5F9',
        color: '#64748B',
        label: status
    };

    if (status === 'Approved') {
        config = {
            background: '#DCFCE7',
            color: '#166534',
            label: 'Approved'
        };
    } else if (status === 'Rejected') {
        config = {
            background: '#FEE2E2',
            color: '#991B1B',
            label: 'Rejected'
        };
    } else if (status === 'Pending') {
        config = {
            background: '#FEF9C3',
            color: '#854D0E',
            label: 'Pending'
        };
    }

    return (
        <Chip
            label={config.label}
            size="small"
            sx={{
                backgroundColor: config.background,
                color: config.color,
                fontWeight: 700,
                fontSize: '11px',
                height: '24px',
                borderRadius: '6px',
                fontFamily: fonts.poppins,
                '& .MuiChip-label': { px: 1 }
            }}
        />
    );
};

const CarbonCreditsManageProofs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [proofs, setProofs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProof, setSelectedProof] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState('All');
    const [actionLoading, setActionLoading] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImage, setViewerImage] = useState('');
    const [zoom, setZoom] = useState(1);

    const fetchProofs = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${url}/api/carbon-credits/admin/proofs`);
            const data = await response.json();
            if (data.success) {
                setProofs(data.proofs || []);
            } else {
                toast.error(data.message || "Failed to fetch proofs");
            }
        } catch (error) {
            console.error("Error fetching proofs:", error);
            toast.error("An error occurred while fetching proofs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProofs();
    }, []);

    const handleUpdateStatus = async (proofId, newStatus) => {
        setActionLoading(true);
        try {
            const response = await fetch(`${url}/api/carbon-credits/proofs/${proofId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await response.json();
            if (response.ok) {
                toast.success(`Proof ${newStatus} successfully`);
                setProofs(proofs.map(p => p._id === proofId ? { ...p, status: newStatus } : p));
                if (selectedProof?._id === proofId) {
                    setSelectedProof({ ...selectedProof, status: newStatus });
                }
            } else {
                toast.error(data.message || "Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("An error occurred while updating status");
        } finally {
            setActionLoading(false);
        }
    };

    const handleViewDetails = (proof) => {
        setSelectedProof(proof);
        setOpenModal(true);
    };

    const filteredProofs = proofs.filter(p => filterStatus === 'All' || p.status === filterStatus);

    return (
        <Box sx={{ p: isMobile ? 2 : 4, maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header Area */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ 
                        fontFamily: fonts.poppins, 
                        fontWeight: 800, 
                        color: colors.primary_dark || '#1A2E05',
                        mb: 1
                    }}>
                        Verify Carbon Proofs
                    </Typography>
                    <Typography sx={{ fontFamily: fonts.poppins, color: colors.text || '#6B7280', fontSize: '1rem' }}>
                        Review and manage carbon credit evidence submitted by farmers.
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, backgroundColor: '#FFFFFF', p: '4px', borderRadius: '12px', boxShadow: '0px 2px 4px rgba(0,0,0,0.05)', border: '1px solid #E0E5EB' }}>
                    {['All', 'Pending', 'Approved', 'Rejected'].map((status) => (
                        <Button
                            key={status}
                            onClick={() => setFilterStatus(status)}
                            size="small"
                            sx={{
                                textTransform: 'none',
                                fontWeight: filterStatus === status ? 700 : 500,
                                fontFamily: fonts.poppins,
                                fontSize: '13px',
                                px: 2,
                                borderRadius: '8px',
                                backgroundColor: filterStatus === status ? colors.primary_light || '#D1E8B9' : 'transparent',
                                color: filterStatus === status ? colors.primary_dark || '#1A2E05' : '#6B7280',
                                '&:hover': {
                                    backgroundColor: filterStatus === status ? colors.primary_light || '#D1E8B9' : '#F3F4F6',
                                }
                            }}
                        >
                            {status}
                        </Button>
                    ))}
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            ) : filteredProofs.length === 0 ? (
                <Card sx={{ borderRadius: '24px', p: 6, textAlign: 'center', border: '1px dashed #E0E5EB', boxShadow: 'none', backgroundColor: 'transparent' }}>
                    <IconAlertCircle size={48} color="#9CA3AF" style={{ marginBottom: '16px' }} />
                    <Typography sx={{ fontFamily: fonts.poppins, color: '#6B7280', fontSize: '1.1rem' }}>
                        No {filterStatus !== 'All' ? filterStatus.toLowerCase() : ''} proofs found.
                    </Typography>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {filteredProofs.map((proof) => (
                        <Grid item xs={12} md={6} lg={4} key={proof._id}>
                            <Card sx={{ 
                                borderRadius: '24px', 
                                border: '1px solid #E0E5EB', 
                                boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
                                transition: 'all 0.2s ease-in-out',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0px 12px 24px rgba(0,0,0,0.06)',
                                    borderColor: colors.primary_light
                                }
                            }}>
                                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ p: 1, backgroundColor: (colors.primary_light || '#D1E8B9') + '40', borderRadius: '10px' }}>
                                                <IconLeaf size={18} color={colors.primary_dark || '#1A2E05'} />
                                            </Box>
                                            <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 700, fontSize: '15px', color: '#111827' }}>
                                                {proof.proofType}
                                            </Typography>
                                        </Box>
                                        <StatusBadge status={proof.status} />
                                    </Box>

                                    <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 600, fontSize: '14px', color: colors.primary_dark, mb: 1 }}>
                                        Project: {proof.projectId?.projectName || 'Untitled Project'}
                                    </Typography>

                                    <Divider sx={{ my: 1.5, borderColor: '#F3F4F6' }} />

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconUser size={16} color="#6B7280" />
                                            <Typography sx={{ fontFamily: fonts.poppins, fontSize: '13px', color: '#4B5563' }}>
                                                {proof.projectId?.userId?.firstName} {proof.projectId?.userId?.lastName}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconMapPin size={16} color="#6B7280" />
                                            <Typography sx={{ fontFamily: fonts.poppins, fontSize: '13px', color: '#4B5563', lineHeight: 1.2 }}>
                                                {proof.projectId?.farmDetails?.district}, {proof.projectId?.farmDetails?.state}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconCalendar size={16} color="#6B7280" />
                                            <Typography sx={{ fontFamily: fonts.poppins, fontSize: '13px', color: '#4B5563' }}>
                                                {new Date(proof.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ mt: 'auto', pt: 3, display: 'flex', gap: 1.5 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => handleViewDetails(proof)}
                                            sx={{
                                                textTransform: 'none',
                                                fontFamily: fonts.poppins,
                                                fontWeight: 600,
                                                borderRadius: '12px',
                                                backgroundColor: '#F3F4F6',
                                                color: '#111827',
                                                boxShadow: 'none',
                                                '&:hover': { backgroundColor: '#E5E7EB', boxShadow: 'none' }
                                            }}
                                            startIcon={<IconEye size={18} />}
                                        >
                                            View Details
                                        </Button>
                                        
                                        {proof.status === 'Pending' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Approve">
                                                    <IconButton 
                                                        onClick={() => handleUpdateStatus(proof._id, 'Approved')}
                                                        sx={{ backgroundColor: '#DCFCE7', color: '#166534', borderRadius: '12px', '&:hover': { backgroundColor: '#BBF7D0' } }}
                                                    >
                                                        <IconCheck size={20} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject">
                                                    <IconButton 
                                                        onClick={() => handleUpdateStatus(proof._id, 'Rejected')}
                                                        sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '12px', '&:hover': { backgroundColor: '#FECACA' } }}
                                                    >
                                                        <IconX size={20} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Proof Details Modal */}
            <Dialog 
                open={openModal} 
                onClose={() => setOpenModal(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: { borderRadius: '28px', p: 0, overflow: 'hidden' }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    fontFamily: fonts.poppins, fontWeight: 800, fontSize: '1.4rem', p: 3,
                    backgroundColor: '#FFFFFF', borderBottom: '1px solid #F1F5F9'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, backgroundColor: colors.primary_light + '30', borderRadius: '12px' }}>
                            <IconLeaf size={24} color={colors.primary_dark} />
                        </Box>
                        Proof Details
                    </Box>
                    <IconButton onClick={() => setOpenModal(false)} sx={{ color: '#94A3B8' }}>
                        <IconX size={24} />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 0 }}>
                    {selectedProof && (
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '500px' }}>
                            {/* Left Side: Media */}
                            <Box sx={{ flex: 1.4, p: 4, backgroundColor: '#FFFFFF' }}>
                                <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 700, fontSize: '16px', color: '#1E293B', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconPhoto size={22} color={colors.primary} /> Evidence Gallery
                                </Typography>
                                
                                <Grid container spacing={3}>
                                    {selectedProof.photos && selectedProof.photos.length > 0 ? selectedProof.photos.map((photo, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Box sx={{ 
                                                position: 'relative', 
                                                width: '100%', 
                                                aspectRatio: '1/1', 
                                                borderRadius: '20px', 
                                                overflow: 'hidden', 
                                                border: '1px solid #F1F5F9',
                                                cursor: 'pointer',
                                                boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
                                                '&:hover img': { transform: 'scale(1.05)' }
                                            }}>
                                                <img 
                                                    src={`${url}/${photo}`} 
                                                    alt={`Proof ${index}`} 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }}
                                                    onClick={() => {
                                                        setViewerImage(`${url}/${photo}`);
                                                        setViewerOpen(true);
                                                        setZoom(1);
                                                    }}
                                                />
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => {
                                                        setViewerImage(`${url}/${photo}`);
                                                        setViewerOpen(true);
                                                        setZoom(1);
                                                    }}
                                                    sx={{ 
                                                        position: 'absolute', top: 12, right: 12,
                                                        backgroundColor: 'rgba(255,255,255,0.9)', 
                                                        backdropFilter: 'blur(4px)',
                                                        '&:hover': { backgroundColor: '#fff' } 
                                                    }}
                                                >
                                                    <IconZoomIn size={16} />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    )) : (
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 8, textAlign: 'center', backgroundColor: '#F8FAFC', borderRadius: '24px', border: '1px dashed #E2E8F0' }}>
                                                <IconPhoto size={48} color="#CBD5E1" />
                                                <Typography sx={{ color: '#64748B', mt: 1, fontFamily: fonts.poppins, fontSize: '15px' }}>No photos uploaded</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>

                                {selectedProof.videoUrl && (
                                    <Box sx={{ mt: 5 }}>
                                        <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 700, mb: 2, fontSize: '16px', color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconVideo size={22} color={colors.primary} /> Video Walkthrough
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            href={selectedProof.videoUrl} 
                                            target="_blank"
                                            startIcon={<IconExternalLink size={18} />}
                                            sx={{ 
                                                textTransform: 'none', borderRadius: '14px', fontFamily: fonts.poppins,
                                                fontWeight: 600, py: 1.5, px: 4, color: colors.primary_dark, borderColor: '#E2E8F0',
                                                '&:hover': { backgroundColor: '#F8FAFC', borderColor: colors.primary }
                                            }}
                                        >
                                            Watch Verification Video
                                        </Button>
                                    </Box>
                                )}

                                {selectedProof.gpsLocation && (
                                    <Box sx={{ mt: 4, p: 2.5, backgroundColor: '#F1F5F9', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <IconMapPin size={24} color={colors.primary} />
                                        <Box>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>GPS Location</Typography>
                                            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1E293B' }}>{selectedProof.gpsLocation}</Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* Right Side: Info */}
                            <Box sx={{ flex: 0.6, p: 4, backgroundColor: '#F8FAFC', borderLeft: '1px solid #F1F5F9' }}>
                                <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 800, mb: 3, fontSize: '1.1rem', color: '#1E293B' }}>
                                    Metadata
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', mb: 0.8, letterSpacing: '0.5px' }}>Proof Category</Typography>
                                        <Chip label={selectedProof.proofType} sx={{ fontWeight: 700, backgroundColor: '#E2E8F0', color: '#475569', borderRadius: '8px' }} />
                                    </Box>
                                    
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', mb: 0.8, letterSpacing: '0.5px' }}>Farmer</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ width: 40, height: 40, borderRadius: '12px', backgroundColor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
                                                {selectedProof.projectId?.userId?.firstName?.charAt(0)}
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>{selectedProof.projectId?.userId?.firstName} {selectedProof.projectId?.userId?.lastName}</Typography>
                                                <Typography sx={{ fontSize: '12px', color: '#64748B' }}>{selectedProof.projectId?.userId?.email}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {selectedProof.additionalNotes && (
                                        <Box>
                                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', mb: 1, letterSpacing: '0.5px' }}>Notes from Farmer</Typography>
                                            <Box sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0px 2px 4px rgba(0,0,0,0.02)' }}>
                                                <Typography sx={{ fontSize: '13px', color: '#475569', fontStyle: 'italic', lineHeight: 1.5 }}>
                                                    "{selectedProof.additionalNotes}"
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', mb: 1, letterSpacing: '0.5px' }}>Status</Typography>
                                        <StatusBadge status={selectedProof.status} />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, backgroundColor: '#FFFFFF', borderTop: '1px solid #F1F5F9', justifyContent: 'center', gap: 2 }}>
                    {selectedProof?.status === 'Pending' ? (
                        <>
                            <Button
                                variant="contained"
                                disabled={actionLoading}
                                onClick={() => handleUpdateStatus(selectedProof._id, 'Approved')}
                                sx={{
                                    backgroundColor: '#10B981',
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    borderRadius: '16px',
                                    textTransform: 'none',
                                    py: 1.5,
                                    px: 5,
                                    fontSize: '15px',
                                    boxShadow: '0px 4px 12px rgba(16, 185, 129, 0.2)',
                                    '&:hover': { backgroundColor: '#059669', boxShadow: '0px 6px 16px rgba(16, 185, 129, 0.3)' }
                                }}
                                startIcon={<IconCheck size={20} />}
                            >
                                Approve Proof
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={actionLoading}
                                onClick={() => handleUpdateStatus(selectedProof._id, 'Rejected')}
                                sx={{
                                    color: '#EF4444',
                                    borderColor: '#FCA5A5',
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    borderRadius: '16px',
                                    textTransform: 'none',
                                    py: 1.5,
                                    px: 5,
                                    fontSize: '15px',
                                    '&:hover': { borderColor: '#EF4444', backgroundColor: '#FEF2F2' }
                                }}
                                startIcon={<IconX size={20} />}
                            >
                                Reject Submission
                            </Button>
                        </>
                    ) : (
                        <Button 
                            onClick={() => setOpenModal(false)}
                            sx={{ fontFamily: fonts.poppins, fontWeight: 600, color: '#64748B', px: 4 }}
                        >
                            Close Details
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Image Viewer Lightbox */}
            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="md"
                PaperProps={{
                    sx: { 
                        backgroundColor: 'rgba(15, 23, 24, 0.98)', 
                        backdropFilter: 'blur(12px)',
                        boxShadow: 'none',
                        borderRadius: '28px',
                        overflow: 'hidden',
                        m: 2,
                        height: '80vh',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
                    {/* Viewer Header */}
                    <Box sx={{ 
                        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                        p: 2, position: 'absolute', top: 0, left: 0, zIndex: 10 
                    }}>
                        <Box sx={{ display: 'flex', gap: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '12px', p: 0.5 }}>
                            <Tooltip title="Zoom In">
                                <IconButton onClick={() => setZoom(prev => Math.min(prev + 0.5, 4))} sx={{ color: '#fff' }}>
                                    <IconZoomIn size={22} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Zoom Out">
                                <IconButton onClick={() => setZoom(prev => Math.max(prev - 0.5, 0.5))} sx={{ color: '#fff' }}>
                                    <IconZoomOut size={22} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset">
                                <IconButton onClick={() => setZoom(1)} sx={{ color: '#fff' }}>
                                    <IconRefresh size={22} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <IconButton onClick={() => setViewerOpen(false)} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}>
                            <IconX size={24} />
                        </IconButton>
                    </Box>

                    {/* Image Container */}
                    <Box sx={{ 
                        width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        overflow: 'auto', p: 4, mt: 6,
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}>
                        <img 
                            src={viewerImage} 
                            alt="Full proof" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '100%', 
                                objectFit: 'contain',
                                transform: `scale(${zoom})`,
                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                borderRadius: '8px'
                            }} 
                        />
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default CarbonCreditsManageProofs;
